import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ClipboardCheck, Route, ContactRound, ArrowUpRight, ArrowRight, CalendarDays, Activity, LockKeyhole, Globe, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/Auth';
import { api, dateLabel, message } from '../lib/api';
import { Loading, PageHeader, Empty } from '../components/shared';
import { CommandTile } from '../components/CommandTile';
import { toast } from 'sonner';

const tileData = [
  { path: '/athletes', title: 'MY ATHLETES', sub: 'Your roster. Their next level.', icon: Users, number: '01', className: 'roster-cover', word: 'THE\nNEXT\nLEVEL.' },
  { path: '/evaluations', title: 'EVALUATION SETUP', sub: 'Measure what moves them forward.', icon: ClipboardCheck, number: '02', className: 'evaluation-cover', word: 'SEE THE\nPOTENTIAL.' },
  { path: '/drills', title: 'DRILL PLAN SETUP', sub: 'Purpose behind every repetition.', icon: Route, number: '03', className: 'drill-cover', word: 'MAKE\nEVERY\nREP COUNT.' },
  { path: '/passport', title: 'TRAINER PASSPORT', sub: 'Your craft. Your calling card.', icon: ContactRound, number: '04', className: 'passport-cover', word: 'COACH\nWITH\nPURPOSE.' },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data)).catch(e => { setError(true); toast.error(message(e)); });
  }, []);
  if (error) return <Empty title="Your workspace couldn't load" text="Please refresh to try again." />;
  if (!data) return <Loading />;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const counts = [`${data.athletes} active athletes`, `${data.templates} templates`, `${data.drill_templates} drill plans`, data.passport?.is_public ? 'Public Passport' : 'Private Passport'];

  return (
    <div className="page-enter dashboard-page">
      <PageHeader eyebrow="YOUR TRAINING GROUND" title={`${greeting}, ${user.name.split(' ')[0]}.`} description="Small steps. Intentional work. Real development.">
        <span className="date-chip"><CalendarDays size={15} />{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
      </PageHeader>
      <section className="stats-strip" aria-label="Workspace stats">
        {[[Users, 'Active athletes', data.athletes], [Activity, 'Sessions logged', data.sessions], [ClipboardCheck, 'Evaluations', data.evaluations], [Route, 'Assigned plans', data.plans]].map(([Icon, label, value], i) => (
          <div className="stat" key={label}><span className={`stat-icon stat-icon-${i}`}><Icon size={18} /></span><div><strong data-testid={`stat-${label.toLowerCase().replaceAll(' ', '-')}`}>{String(value).padStart(2, '0')}</strong><span>{label}</span></div><span className="stat-rule" /></div>
        ))}
      </section>
      <section className="tools-section">
        <div className="section-heading"><h2 data-testid="tools-heading">Your workspace</h2><span className="section-index">FOUR TOOLS. ONE PURPOSE.</span></div>
        <div className="album-grid">{tileData.map((tile, i) => <CommandTile key={tile.path} tile={tile} index={i} count={counts[i]} isPublic={data.passport?.is_public} />)}</div>
      </section>
      <section className="dashboard-bottom">
        <div className="activity-section">
          <div className="section-heading"><h2>Recent activity</h2><Link to="/athletes" data-testid="view-athletes-link">View athletes<ArrowRight size={14} /></Link></div>
          {data.recent_sessions.length ? data.recent_sessions.map(s => (
            <Link data-testid={`recent-session-${s.id}`} to={`/athletes/${s.athlete_id}?tab=sessions`} className="activity-row" key={s.id}>
              <span className="activity-icon"><Activity size={18} /></span><div><strong>{s.title}</strong><p>{s.athlete_name} · {s.duration} min</p></div><span>{dateLabel(s.date)}</span><ChevronRight size={16} />
            </Link>
          )) : (
            <div className="quiet-empty"><Activity size={22} /><div><strong>Your next session starts a story.</strong><p>Logged sessions will appear here.</p></div><Link to="/athletes" data-testid="start-coaching-link" className="text-link">Go to athletes<ArrowUpRight size={15} /></Link></div>
          )}
        </div>
        <div className="passport-status">
          <span className="eyebrow">YOUR CONNECTION POINT</span>
          <div><h2>Good coaching starts<br />with a connection.</h2><span className="passport-mini-icon"><ContactRound size={30} /></span></div>
          <Link to="/passport" data-testid="dashboard-passport-link">{data.passport?.is_public ? 'Share your Passport' : 'Set up your Passport'}<ArrowUpRight size={16} /></Link>
          <span className="passport-status-note">{data.passport?.is_public ? <Globe size={12} /> : <LockKeyhole size={12} />} {data.passport?.is_public ? 'Public and ready to share' : 'Private until you choose to share'}</span>
        </div>
      </section>
    </div>
  );
}